// src/pages/Admin.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ImageViewer from '../components/ImageViewer';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';
import { useInputDialog } from '../components/InputModal';
import SubNav from '../components/ui/SubNav';
import { colors } from '../theme/tokens';

const Admin = () => {
  const { token, canApprove, canManageTiers, user } = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();
  const confirm = useConfirm();
  const showInput = useInputDialog();

  const [stats, setStats] = useState(null);
  const [pendingCars, setPendingCars] = useState([]);
  const [users, setUsers] = useState([]);
  const [makeModelRequests, setMakeModelRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [selectedPartCode, setSelectedPartCode] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    if (!canApprove) {
      navigate('/');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canApprove, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, pendingRes, usersRes, makeModelRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/pending-cars`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/make-model-requests/pending`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setStats(statsRes.data);
      setPendingCars(pendingRes.data);
      setUsers(usersRes.data);
      setMakeModelRequests(makeModelRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      showToast('Failed to load admin data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const setLoading2 = (key, val) => setActionLoading(prev => ({ ...prev, [key]: val }));

  const approvePart = async (carId, partId) => {
    const key = `approve-${partId}`;
    setLoading2(key, true);
    try {
      await axios.patch(`${API_URL}/api/admin/approve-part/${carId}/${partId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Part approved! Token awarded to submitter.', 'success');
      fetchData();
    } catch (error) {
      showToast('Failed to approve part: ' + error.response?.data?.error, 'error');
    } finally {
      setLoading2(key, false);
    }
  };

  const rejectPart = async (carId, partId) => {
    const ok = await confirm('Are you sure you want to reject this part?', {
      title: 'Reject Part',
      icon: '🗑️',
      confirmText: 'Reject',
      danger: true
    });
    if (!ok) return;

    const key = `reject-${partId}`;
    setLoading2(key, true);
    try {
      await axios.delete(`${API_URL}/api/admin/reject-part/${carId}/${partId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Part rejected and removed.', 'info');
      fetchData();
    } catch (error) {
      showToast('Failed to reject part: ' + error.response?.data?.error, 'error');
    } finally {
      setLoading2(key, false);
    }
  };

  const updateUserTier = async (userId, newTier) => {
    const key = `tier-${userId}`;
    setLoading2(key, true);
    try {
      await axios.patch(`${API_URL}/api/admin/users/${userId}/tier`, 
        { tier: newTier },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      showToast(`User tier updated to ${newTier}`, 'success');
      fetchData();
    } catch (error) {
      showToast('Failed to update tier: ' + error.response?.data?.error, 'error');
    } finally {
      setLoading2(key, false);
    }
  };

  const awardTokens = async (userId) => {
    const amount = await showInput('How many tokens do you want to award?', {
      title: 'Award Tokens',
      icon: '🪙',
      placeholder: 'e.g. 10',
      inputType: 'number',
      hint: 'Enter a positive number',
      confirmText: 'Award',
      required: true
    });
    if (!amount || isNaN(amount)) return;

    const key = `tokens-${userId}`;
    setLoading2(key, true);
    try {
      await axios.patch(`${API_URL}/api/admin/users/${userId}/tokens`,
        { tokens: parseInt(amount) },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      showToast(`Awarded ${amount} tokens!`, 'success');
      fetchData();
    } catch (error) {
      showToast('Failed to award tokens: ' + error.response?.data?.error, 'error');
    } finally {
      setLoading2(key, false);
    }
  };

  const updateSalesRole = async (userId, newSalesRole) => {
    const key = `role-${userId}`;
    setLoading2(key, true);
    try {
      const roleValue = newSalesRole === '' ? null : newSalesRole;
      await axios.patch(`${API_URL}/api/admin/users/${userId}/sales-role`, 
        { salesRole: roleValue },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      const isUpdatingSelf = user?._id === userId;
      if (isUpdatingSelf) {
        showToast(`Sales role updated to ${roleValue || 'none'}. Log out and back in to see changes.`, 'info');
      } else {
        showToast(`Sales role updated to ${roleValue || 'none'}`, 'success');
      }
      fetchData();
    } catch (error) {
      console.error('Sales role update error:', error);
      showToast('Failed to update sales role: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading2(key, false);
    }
  };

  const updateInboxAccess = async (userId, canAccessInbox) => {
    const key = `inbox-${userId}`;
    setLoading2(key, true);
    try {
      await axios.patch(`${API_URL}/api/admin/users/${userId}/inbox-access`,
        { canAccessInbox },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      showToast(`Inbox access ${canAccessInbox ? 'granted' : 'removed'}`, 'success');
      fetchData();
    } catch (error) {
      showToast('Failed to update inbox access: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading2(key, false);
    }
  };

  const updateNfcAccess = async (userId, canAccessNfc) => {
    const key = `nfc-${userId}`;
    setLoading2(key, true);
    try {
      await axios.patch(`${API_URL}/api/admin/users/${userId}/nfc-access`,
        { canAccessNfc },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      showToast(`NFC card access ${canAccessNfc ? 'granted' : 'removed'}`, 'success');
      fetchData();
    } catch (error) {
      showToast('Failed to update NFC access: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading2(key, false);
    }
  };

  const updateTasksAccess = async (userId, canAccessTasks) => {
    const key = `tasks-${userId}`;
    setLoading2(key, true);
    try {
      await axios.patch(`${API_URL}/api/admin/users/${userId}/tasks-access`,
        { canAccessTasks },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      showToast(`Task tracking access ${canAccessTasks ? 'granted' : 'removed'}`, 'success');
      fetchData();
    } catch (error) {
      showToast('Failed to update task tracking access: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading2(key, false);
    }
  };

  const ghostUser = async (userId, userName) => {
    const ok = await confirm(`Ghost "${userName}"?\n\nThey will be unable to log in until restored. All their data is preserved.`, {
      title: 'Ghost User',
      icon: '👻',
      confirmText: 'Ghost',
      danger: true
    });
    if (!ok) return;
    const key = `ghost-${userId}`;
    setLoading2(key, true);
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/ghost`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast(`${userName} has been ghosted`, 'success');
      fetchData();
    } catch (error) {
      showToast('Failed to ghost user: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading2(key, false);
    }
  };

  const unghostUser = async (userId, userName) => {
    const ok = await confirm(`Restore "${userName}"? They will be able to log in again.`, {
      title: 'Restore User',
      icon: '✅',
      confirmText: 'Restore'
    });
    if (!ok) return;
    const key = `ghost-${userId}`;
    setLoading2(key, true);
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/unghost`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast(`${userName} has been restored`, 'success');
      fetchData();
    } catch (error) {
      showToast('Failed to restore user: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading2(key, false);
    }
  };

  const updateNotificationEmail = async (userId, notificationEmail) => {
    const key = `notif-${userId}`;
    setLoading2(key, true);
    try {
      await axios.patch(`${API_URL}/api/admin/users/${userId}/notification-email`,
        { notificationEmail },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      showToast(notificationEmail ? 'Reminder email updated' : 'Reminder email cleared (using login email)', 'success');
      fetchData();
    } catch (error) {
      showToast('Failed to update reminder email: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading2(key, false);
    }
  };

  const approveMakeModelRequest = async (requestId) => {
    const key = `mmapprove-${requestId}`;
    setLoading2(key, true);
    try {
      await axios.patch(`${API_URL}/api/make-model-requests/approve/${requestId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Request approved! Make/Model added to car list.', 'success');
      fetchData();
    } catch (error) {
      showToast('Failed to approve request: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading2(key, false);
    }
  };

  const rejectMakeModelRequest = async (requestId) => {
    const reason = await showInput('Provide a rejection reason (optional):', {
      title: 'Reject Make/Model Request',
      icon: '🗑️',
      placeholder: 'Reason (optional)',
      confirmText: 'Reject'
    });
    if (reason === null) return;

    const key = `mmreject-${requestId}`;
    setLoading2(key, true);
    try {
      await axios.delete(`${API_URL}/api/make-model-requests/reject/${requestId}`, {
        data: { reason: reason || '' },
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Request rejected.', 'info');
      fetchData();
    } catch (error) {
      showToast('Failed to reject request: ' + (error.response?.data?.error || error.message), 'error');
    } finally {
      setLoading2(key, false);
    }
  };

  if (loading) return <div style={styles.loading}>Loading admin dashboard...</div>;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>{canManageTiers ? '🔴 Rubber Superuser Dashboard' : '💎 Platinum Admin Dashboard'}</h1>
      
      {/* Stats Cards */}
      {stats && (
        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{stats.users.total}</div>
            <div style={styles.statLabel}>Total Users</div>
            <div style={styles.statBreakdown}>
              🥈 {stats.users.silver} | 🥇 {stats.users.gold} | 💎 {stats.users.platinum} {stats.users.rubber > 0 && `| 🔴 ${stats.users.rubber}`}
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{stats.parts?.pending || 0}</div>
            <div style={styles.statLabel}>Pending Parts</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{stats.parts?.approved || 0}</div>
            <div style={styles.statLabel}>Approved Parts</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{stats.cars?.total || 0}</div>
            <div style={styles.statLabel}>Total Cars</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <SubNav
        align="start"
        items={[
          { key: 'pending', label: 'Pending Submissions', icon: 'inventory_2', count: pendingCars.length, active: activeTab === 'pending', onClick: () => setActiveTab('pending') },
          { key: 'make-model', label: 'Make/Model Requests', icon: 'directions_car', count: makeModelRequests.length, active: activeTab === 'make-model', onClick: () => setActiveTab('make-model') },
          { key: 'users', label: 'Manage Users', icon: 'group', count: users.length, active: activeTab === 'users', onClick: () => setActiveTab('users') },
          { key: 'emails', label: 'Email Notifications', icon: 'mail', active: activeTab === 'emails', onClick: () => setActiveTab('emails') },
          ...(canManageTiers ? [{ key: 'lead-actions', label: 'Lead Actions', icon: 'ads_click', active: activeTab === 'lead-actions', onClick: () => setActiveTab('lead-actions') }] : [])
        ]}
        style={{ padding: '12px 0', marginBottom: 12 }}
      />

      {/* Pending Parts */}
      {activeTab === 'pending' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Pending Part Submissions</h2>
          {pendingCars.length === 0 ? (
            <p style={styles.emptyState}>No pending parts! 🎉</p>
          ) : (
            pendingCars.map(car => (
              <div key={car._id} style={styles.card}>
                <div style={styles.cardHeader}>
                  <div>
                    <h3>
                      {car.isCustomMake && <span style={styles.customBadge}>🆕 NEW MAKE</span>}
                      {car.make} {car.model} 
                      {car.isCustomModel && <span style={styles.customBadge}>🆕 NEW MODEL</span>}
                      {' '}{car.variant}
                    </h3>
                    {(car.isCustomMake || car.isCustomModel) && (
                      <p style={styles.customNote}>
                        💡 This submission includes a new make/model that will be added to the dropdown list when approved
                      </p>
                    )}
                  </div>
                  <span style={styles.yearRange}>
                    {car.yearRange.from} - {car.yearRange.to}
                  </span>
                </div>
                <div style={styles.cardBody}>
                  <p style={{marginBottom: '10px', color: colors.textMuted}}>
                    <strong>Car Status:</strong> {car.approvedParts} approved parts | {car.pendingParts.length} pending approval
                  </p>
                  <p><strong>Pending Parts to Review:</strong></p>
                  <div style={styles.partsGrid}>
                    {car.pendingParts.map((part) => (
                      <div key={part._id} style={styles.partCard}>
                        <div style={styles.partInfo}>
                          <strong>{part.position}</strong>
                          <div>
                            Code: <span 
                              style={styles.partCodeLink}
                              onClick={() => setSelectedPartCode(part.code)}
                              title="Click to view product image"
                            >
                              {part.code}
                            </span>
                          </div>
                          <div>Qty: {part.quantity}{part.unit}</div>
                          <div style={{fontSize: '0.85em', color: colors.textMuted, marginTop: '5px'}}>
                            {part.isFromRequest ? (
                              <>
                                <div style={{background: colors.amberSoft, padding: '4px 8px', borderRadius: '4px', marginBottom: '5px', border: '1px solid #91C83E'}}>
                                  <strong style={{color: '#2fb65c'}}>📋 REQUESTED PART</strong>
                                </div>
                                <div>Fulfilled by: {part.addedByUser?.name} ({part.addedByUser?.tier.toUpperCase()})</div>
                                {part.requestedByUser && (
                                  <div>Requested by: {part.requestedByUser?.name} ({part.requestedByUser?.tier.toUpperCase()})</div>
                                )}
                              </>
                            ) : (
                              <div>Added by: {part.addedByUser?.name} ({part.addedByUser?.tier.toUpperCase()})</div>
                            )}
                          </div>
                        </div>
                        <div style={styles.partActions}>
                          <button 
                            onClick={() => approvePart(car._id, part._id)} 
                            disabled={actionLoading[`approve-${part._id}`]}
                            style={{...styles.btn, ...styles.approveBtn, padding: '5px 10px', fontSize: '0.9em',
                              opacity: actionLoading[`approve-${part._id}`] ? 0.6 : 1}}
                          >
                            {actionLoading[`approve-${part._id}`] ? '...' : '✓ Approve'}
                          </button>
                          <button 
                            onClick={() => rejectPart(car._id, part._id)} 
                            disabled={actionLoading[`reject-${part._id}`]}
                            style={{...styles.btn, ...styles.rejectBtn, padding: '5px 10px', fontSize: '0.9em',
                              opacity: actionLoading[`reject-${part._id}`] ? 0.6 : 1}}
                          >
                            {actionLoading[`reject-${part._id}`] ? '...' : '✗ Reject'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Make/Model Requests */}
      {activeTab === 'make-model' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Make/Model Requests</h2>
          {makeModelRequests.length === 0 ? (
            <div style={styles.noPending}>No pending make/model requests</div>
          ) : (
            <div style={styles.requestsGrid}>
              {makeModelRequests.map(request => (
                <div key={request._id} style={styles.requestCard}>
                  <div style={styles.requestHeader}>
                    <div style={styles.requestType}>
                      {request.type === 'make' ? '🏭' : '🚗'} {request.type.toUpperCase()}
                    </div>
                    <div style={styles.requestDate}>
                      {new Date(request.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={styles.requestInfo}>
                    <div style={styles.requestMake}>
                      <strong>Make:</strong> {request.make}
                    </div>
                    {request.type === 'model' && (
                      <div style={styles.requestModel}>
                        <strong>Model:</strong> {request.model}
                      </div>
                    )}
                  </div>
                  <div style={styles.requestUser}>
                    <strong>Requested by:</strong> {request.requestedBy?.name || 'Unknown'}
                    <span style={styles.requestUserEmail}>({request.requestedBy?.email})</span>
                  </div>
                  <div style={styles.requestActions}>
                    <button
                      onClick={() => approveMakeModelRequest(request._id)}
                      disabled={actionLoading[`mmapprove-${request._id}`]}
                      style={{...styles.approveBtn, opacity: actionLoading[`mmapprove-${request._id}`] ? 0.6 : 1}}
                    >
                      {actionLoading[`mmapprove-${request._id}`] ? '...' : '✓ Approve'}
                    </button>
                    <button
                      onClick={() => rejectMakeModelRequest(request._id)}
                      disabled={actionLoading[`mmreject-${request._id}`]}
                      style={{...styles.rejectBtn, opacity: actionLoading[`mmreject-${request._id}`] ? 0.6 : 1}}
                    >
                      {actionLoading[`mmreject-${request._id}`] ? '...' : '✗ Reject'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'users' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>User Management</h2>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Tier</th>
                  <th style={styles.th}>Sales Role</th>
                  <th style={styles.th}>Inbox Access</th>
                  <th style={styles.th}>NFC Card</th>
                  <th style={styles.th}>Task Tracking</th>
                  <th style={styles.th}>Approved Parts</th>
                  <th style={styles.th}>Tokens</th>
                  <th style={styles.th}>Searches Today</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id} style={styles.tr}>
                    <td style={styles.td}>
                      <div style={styles.userCell}>
                        {u.picture && <img src={u.picture} alt={u.name} style={styles.userAvatar} />}
                        {u.name}
                      </div>
                    </td>
                    <td style={styles.td}>{u.email}</td>
                    <td style={styles.td}>
                      {canManageTiers ? (
                        <div style={{ position: 'relative' }}>
                          <select 
                            value={u.tier} 
                            onChange={(e) => updateUserTier(u._id, e.target.value)}
                            style={{...styles.select, opacity: actionLoading[`tier-${u._id}`] ? 0.6 : 1}}
                            disabled={actionLoading[`tier-${u._id}`]}
                          >
                            <option value="silver">🥈 Silver</option>
                            <option value="gold">🥇 Gold</option>
                            <option value="platinum">💎 Platinum</option>
                            <option value="rubber">🔴 Rubber</option>
                          </select>
                          {actionLoading[`tier-${u._id}`] && <span style={styles.inlineSpinner}>⏳</span>}
                        </div>
                      ) : (
                        <span style={styles.readOnlyTier}>
                          {u.tier === 'rubber' && '🔴 Rubber'}
                          {u.tier === 'platinum' && '💎 Platinum'}
                          {u.tier === 'gold' && '🥇 Gold'}
                          {u.tier === 'silver' && '🥈 Silver'}
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {canManageTiers ? (
                        <div style={{ position: 'relative' }}>
                          <select 
                            value={u.salesRole || ''} 
                            onChange={(e) => updateSalesRole(u._id, e.target.value)}
                            style={{...styles.select, opacity: actionLoading[`role-${u._id}`] ? 0.6 : 1}}
                            disabled={actionLoading[`role-${u._id}`]}
                          >
                            <option value="">None</option>
                            <option value="rep">📊 Sales Rep</option>
                            <option value="manager">👔 Sales Manager</option>
                          </select>
                          {actionLoading[`role-${u._id}`] && <span style={styles.inlineSpinner}>⏳</span>}
                        </div>
                      ) : (
                        <span style={styles.readOnlySalesRole}>
                          {u.salesRole === 'manager' && '👔 Sales Manager'}
                          {u.salesRole === 'rep' && '📊 Sales Rep'}
                          {!u.salesRole && 'None'}
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {canManageTiers ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            onClick={() => updateInboxAccess(u._id, !u.canAccessInbox)}
                            disabled={actionLoading[`inbox-${u._id}`]}
                            style={{
                              ...styles.inboxToggleBtn,
                              background: u.canAccessInbox ? '#2fb65c' : colors.surface2,
                              color: u.canAccessInbox ? 'white' : colors.textMuted,
                              opacity: actionLoading[`inbox-${u._id}`] ? 0.6 : 1
                            }}
                          >
                            {actionLoading[`inbox-${u._id}`] ? '...' : u.canAccessInbox ? '💬 On' : 'Off'}
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontSize: '13px', color: u.canAccessInbox ? '#2fb65c' : colors.textMuted }}>
                          {u.canAccessInbox ? '💬 Enabled' : 'Disabled'}
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {canManageTiers ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            onClick={() => updateNfcAccess(u._id, !u.canAccessNfc)}
                            disabled={actionLoading[`nfc-${u._id}`]}
                            style={{
                              ...styles.inboxToggleBtn,
                              background: u.canAccessNfc ? '#2fb65c' : colors.surface2,
                              color: u.canAccessNfc ? 'white' : colors.textMuted,
                              opacity: actionLoading[`nfc-${u._id}`] ? 0.6 : 1
                            }}
                          >
                            {actionLoading[`nfc-${u._id}`] ? '...' : u.canAccessNfc ? '📇 On' : 'Off'}
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontSize: '13px', color: u.canAccessNfc ? '#2fb65c' : colors.textMuted }}>
                          {u.canAccessNfc ? '📇 Enabled' : 'Disabled'}
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {canManageTiers ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <button
                            onClick={() => updateTasksAccess(u._id, !u.canAccessTasks)}
                            disabled={actionLoading[`tasks-${u._id}`]}
                            style={{
                              ...styles.inboxToggleBtn,
                              background: u.canAccessTasks ? '#2fb65c' : colors.surface2,
                              color: u.canAccessTasks ? 'white' : colors.textMuted,
                              opacity: actionLoading[`tasks-${u._id}`] ? 0.6 : 1
                            }}
                          >
                            {actionLoading[`tasks-${u._id}`] ? '...' : u.canAccessTasks ? '✔ On' : 'Off'}
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontSize: '13px', color: u.canAccessTasks ? '#2fb65c' : colors.textMuted }}>
                          {u.canAccessTasks ? '✔ Enabled' : 'Disabled'}
                        </span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.statBadge}>✓ {u.approvedSubmissions || 0}</span>
                    </td>
                    <td style={styles.td}>🪙 {u.tokens}</td>
                    <td style={styles.td}>{u.dailySearchCount} / {u.tier === 'silver' ? '5' : '∞'}</td>
                    <td style={styles.td}>
                      {u.isGhosted
                        ? <span style={{ color: '#e74c3c', fontWeight: 700, fontSize: 13 }}>👻 Ghosted</span>
                        : <span style={{ color: '#2fb65c', fontSize: 13 }}>● Active</span>
                      }
                    </td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button 
                          onClick={() => awardTokens(u._id)} 
                          disabled={actionLoading[`tokens-${u._id}`]}
                          style={{...styles.tokenBtn, opacity: actionLoading[`tokens-${u._id}`] ? 0.6 : 1}}
                        >
                          {actionLoading[`tokens-${u._id}`] ? '...' : 'Award Tokens'}
                        </button>
                        {canManageTiers && u.tier !== 'rubber' && u._id !== user?._id && (
                          u.isGhosted ? (
                            <button
                              onClick={() => unghostUser(u._id, u.name)}
                              disabled={actionLoading[`ghost-${u._id}`]}
                              style={{ ...styles.tokenBtn, background: '#2fb65c', color: 'white', opacity: actionLoading[`ghost-${u._id}`] ? 0.6 : 1 }}
                            >
                              {actionLoading[`ghost-${u._id}`] ? '...' : 'Restore'}
                            </button>
                          ) : (
                            <button
                              onClick={() => ghostUser(u._id, u.name)}
                              disabled={actionLoading[`ghost-${u._id}`]}
                              style={{ ...styles.tokenBtn, background: '#e74c3c', color: 'white', opacity: actionLoading[`ghost-${u._id}`] ? 0.6 : 1 }}
                            >
                              {actionLoading[`ghost-${u._id}`] ? '...' : 'Ghost'}
                            </button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Email Notifications */}
      {activeTab === 'emails' && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Email Notifications</h2>
          <p style={{ color: colors.textMuted, marginTop: -8, marginBottom: 20, fontSize: 14, lineHeight: 1.5 }}>
            Task notifications (new task, deadline reminder, and chat mentions) are sent to each person below.
            Set a preferred address per user, or leave it blank to use their sign-in email.
            Only users with Task Tracking access are listed.
          </p>
          {(() => {
            const taskUsers = users.filter((u) => u.canAccessTasks);
            if (taskUsers.length === 0) {
              return (
                <p style={styles.emptyState}>
                  No users have Task Tracking access yet. Enable it under Manage Users first.
                </p>
              );
            }
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {taskUsers.map((u) => (
                  <div key={u._id} style={styles.emailRow}>
                    <div style={styles.emailUser}>
                      {u.picture
                        ? <img src={u.picture} alt="" style={styles.emailAvatar} />
                        : <span style={styles.emailAvatarFallback}>{(u.name || '?').charAt(0).toUpperCase()}</span>}
                      <div style={{ minWidth: 0 }}>
                        <div style={styles.emailName}>{u.name}</div>
                        <div style={styles.emailLogin}>Sign-in: {u.email}</div>
                      </div>
                    </div>
                    <div style={styles.emailEditor}>
                      <NotifEmailCell
                        user={u}
                        editable={canManageTiers}
                        loading={actionLoading[`notif-${u._id}`]}
                        onSave={updateNotificationEmail}
                      />
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Lead Actions (Rubber only) */}
      {activeTab === 'lead-actions' && canManageTiers && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Lead Quick Actions</h2>
          <p style={{ color: colors.textMuted, marginTop: -8, marginBottom: 20, fontSize: 14, lineHeight: 1.5 }}>
            Actions reps can log from a lead's chat on the client map. Each one posts a note and
            earns its point value in the analytics. Add, edit, reorder or remove them here — no code change needed.
          </p>
          <LeadActionsManager token={token} apiUrl={API_URL} showToast={showToast} confirm={confirm} />
        </div>
      )}

      {/* Image Viewer Modal */}
      {selectedPartCode && (
        <ImageViewer 
          partCode={selectedPartCode} 
          onClose={() => setSelectedPartCode(null)} 
        />
      )}
    </div>
  );
};

// Rubber-only manager for the configurable lead quick actions. Self-contained:
// fetches its own list and does the CRUD against /api/admin/lead-actions.
const LeadActionsManager = ({ token, apiUrl, showToast, confirm }) => {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ label: '', icon: '📌', points: 1 });
  const headers = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/admin/lead-actions`, headers);
      setActions(res.data);
    } catch (err) {
      showToast('Failed to load lead actions', 'error');
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.label.trim()) { showToast('Enter a label', 'error'); return; }
    setBusy(true);
    try {
      await axios.post(`${apiUrl}/api/admin/lead-actions`, {
        label: draft.label.trim(),
        icon: (draft.icon || '📌').trim(),
        points: Number(draft.points) || 0
      }, headers);
      setDraft({ label: '', icon: '📌', points: 1 });
      await load();
      showToast('Action added', 'success');
    } catch (err) {
      showToast('Failed to add action: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setBusy(false);
    }
  };

  const save = async (a, patch) => {
    setBusy(true);
    try {
      await axios.patch(`${apiUrl}/api/admin/lead-actions/${a._id}`, patch, headers);
      await load();
    } catch (err) {
      showToast('Failed to save: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    const ok = await confirm(`Delete "${a.label}"? Past analytics are kept; it just disappears from the rep dropdown.`, {
      title: 'Delete lead action', icon: '🗑️', confirmText: 'Delete', danger: true
    });
    if (!ok) return;
    setBusy(true);
    try {
      await axios.delete(`${apiUrl}/api/admin/lead-actions/${a._id}`, headers);
      await load();
      showToast('Action deleted', 'success');
    } catch (err) {
      showToast('Failed to delete: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setBusy(false);
    }
  };

  // Swap display order with the neighbour in the given direction.
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= actions.length) return;
    const a = actions[idx], b = actions[j];
    setBusy(true);
    try {
      await Promise.all([
        axios.patch(`${apiUrl}/api/admin/lead-actions/${a._id}`, { order: b.order }, headers),
        axios.patch(`${apiUrl}/api/admin/lead-actions/${b._id}`, { order: a.order }, headers)
      ]);
      await load();
    } catch (err) {
      showToast('Failed to reorder', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p style={styles.emptyState}>Loading actions…</p>;

  const inp = {
    background: colors.surface, color: colors.text, border: `1px solid ${colors.border}`,
    borderRadius: 8, padding: '7px 10px', fontSize: 13
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {actions.map((a, i) => (
        <LeadActionRow
          key={a._id}
          action={a}
          first={i === 0}
          last={i === actions.length - 1}
          busy={busy}
          inp={inp}
          onSave={(patch) => save(a, patch)}
          onDelete={() => remove(a)}
          onUp={() => move(i, -1)}
          onDown={() => move(i, 1)}
        />
      ))}

      {/* New action */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8, paddingTop: 14, borderTop: `1px solid ${colors.border}` }}>
        <input
          value={draft.icon}
          onChange={(e) => setDraft({ ...draft, icon: e.target.value })}
          style={{ ...inp, width: 48, textAlign: 'center' }}
          aria-label="Icon"
        />
        <input
          value={draft.label}
          onChange={(e) => setDraft({ ...draft, label: e.target.value })}
          placeholder="New action label (e.g. Demo booked)"
          style={{ ...inp, flex: 1, minWidth: 160 }}
        />
        <input
          type="number"
          min={0}
          value={draft.points}
          onChange={(e) => setDraft({ ...draft, points: e.target.value })}
          style={{ ...inp, width: 72 }}
          aria-label="Points"
          title="Points"
        />
        <button
          onClick={create}
          disabled={busy}
          style={{ background: colors.parGreen, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, fontSize: 13, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}
        >
          + Add
        </button>
      </div>
    </div>
  );
};

// One editable row in the lead-actions manager.
const LeadActionRow = ({ action, first, last, busy, inp, onSave, onDelete, onUp, onDown }) => {
  const [label, setLabel] = useState(action.label);
  const [icon, setIcon] = useState(action.icon || '📌');
  const [points, setPoints] = useState(action.points);
  useEffect(() => { setLabel(action.label); setIcon(action.icon || '📌'); setPoints(action.points); }, [action._id, action.label, action.icon, action.points]);

  const dirty = label.trim() !== action.label || (icon || '').trim() !== (action.icon || '') || Number(points) !== action.points;
  const iconBtn = { background: colors.surface, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 8, width: 30, height: 30, cursor: 'pointer', fontSize: 14, lineHeight: 1 };

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', opacity: action.enabled ? 1 : 0.55 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <button onClick={onUp} disabled={busy || first} style={{ ...iconBtn, height: 15, opacity: first ? 0.3 : 1 }} title="Move up">▲</button>
        <button onClick={onDown} disabled={busy || last} style={{ ...iconBtn, height: 15, opacity: last ? 0.3 : 1 }} title="Move down">▼</button>
      </div>
      <input value={icon} onChange={(e) => setIcon(e.target.value)} style={{ ...inp, width: 48, textAlign: 'center' }} aria-label="Icon" />
      <input value={label} onChange={(e) => setLabel(e.target.value)} style={{ ...inp, flex: 1, minWidth: 160 }} aria-label="Label" />
      <input type="number" min={0} value={points} onChange={(e) => setPoints(e.target.value)} style={{ ...inp, width: 72 }} aria-label="Points" title="Points" />
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: colors.textMuted, cursor: 'pointer' }} title="Show in the rep dropdown">
        <input type="checkbox" checked={action.enabled} disabled={busy} onChange={(e) => onSave({ enabled: e.target.checked })} />
        On
      </label>
      <button
        onClick={() => onSave({ label: label.trim(), icon: (icon || '📌').trim(), points: Number(points) || 0 })}
        disabled={busy || !dirty}
        style={{ background: dirty ? colors.parGreen : colors.surface, color: dirty ? '#fff' : colors.textMuted, border: `1px solid ${dirty ? colors.parGreen : colors.border}`, borderRadius: 8, padding: '7px 14px', fontWeight: 700, fontSize: 12, cursor: (busy || !dirty) ? 'default' : 'pointer' }}
      >
        Save
      </button>
      <button
        onClick={onDelete}
        disabled={busy}
        style={{ background: 'transparent', color: '#e5544b', border: `1px solid ${colors.border}`, borderRadius: 8, padding: '7px 12px', fontWeight: 700, fontSize: 12, cursor: busy ? 'default' : 'pointer' }}
        title="Delete action"
      >
        Delete
      </button>
    </div>
  );
};

// Inline editor for a user's reminder (notification) email. Admins can set an
// address different from the login email, or clear it to fall back to login.
const NotifEmailCell = ({ user, editable, loading, onSave }) => {
  const [val, setVal] = useState(user.notificationEmail || '');
  useEffect(() => { setVal(user.notificationEmail || ''); }, [user.notificationEmail]);
  const dirty = val.trim() !== (user.notificationEmail || '');

  if (!editable) {
    return (
      <span style={{ fontSize: '13px', color: colors.textMuted }}>
        {user.notificationEmail || user.email}
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <input
        type="email"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder={user.email}
        title={`Blank = use login email (${user.email})`}
        style={{
          width: '160px',
          padding: '6px 8px',
          borderRadius: '8px',
          border: `1px solid ${colors.glassBorder}`,
          background: colors.surface2,
          color: colors.text,
          fontSize: '12px'
        }}
      />
      <button
        onClick={() => onSave(user._id, val.trim())}
        disabled={loading || !dirty}
        style={{
          ...styles.inboxToggleBtn,
          background: dirty ? '#2fb65c' : colors.surface2,
          color: dirty ? 'white' : colors.textMuted,
          opacity: loading || !dirty ? 0.6 : 1
        }}
      >
        {loading ? '...' : 'Save'}
      </button>
    </div>
  );
};

const styles = {
  container: { maxWidth: '1200px', margin: '0 auto', padding: '4px 16px 24px', color: colors.text },
  loading: { textAlign: 'center', padding: '50px', fontSize: '18px', color: colors.textMuted },
  title: { textAlign: 'center', color: colors.text, marginBottom: '24px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '24px' },
  statCard: { background: colors.glass, border: `1px solid ${colors.glassBorder}`, padding: '20px', borderRadius: '14px', boxShadow: '0 8px 24px rgba(0,0,0,0.35)', textAlign: 'center' },
  statNumber: { fontSize: '36px', fontWeight: 'bold', color: colors.parGreenBright },
  statLabel: { fontSize: '14px', color: colors.textMuted, marginTop: '5px' },
  statBreakdown: { fontSize: '12px', color: colors.textFaint, marginTop: '10px' },
  section: { background: colors.glass, border: `1px solid ${colors.glassBorder}`, padding: '20px', borderRadius: '14px', boxShadow: '0 8px 24px rgba(0,0,0,0.35)' },
  sectionTitle: { marginBottom: '20px', color: colors.text },
  emailRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', padding: '12px 14px', borderRadius: '12px', border: `1px solid ${colors.glassBorder}`, background: colors.surfaceSoft },
  emailUser: { display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 },
  emailAvatar: { width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 },
  emailAvatarFallback: { width: 36, height: 36, borderRadius: '50%', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: colors.surface2, color: colors.text, fontWeight: 700 },
  emailName: { fontWeight: 700, fontSize: 14, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  emailLogin: { fontSize: 12, color: colors.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  emailEditor: { flexShrink: 0 },
  emptyState: { textAlign: 'center', padding: '40px', color: colors.textMuted, fontSize: '18px' },
  noPending: { textAlign: 'center', padding: '40px', color: colors.textMuted, fontSize: '16px' },
  card: { border: `1px solid ${colors.glassBorder}`, borderRadius: '12px', padding: '20px', marginBottom: '15px', background: colors.surfaceSoft },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' },
  yearRange: { background: 'rgba(47,182,92,0.16)', padding: '5px 15px', borderRadius: '15px', fontSize: '14px', color: colors.parGreenBright },
  cardBody: { marginBottom: '15px' },
  partsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px', marginTop: '15px' },
  partCard: { border: `1px solid ${colors.glassBorder}`, borderRadius: '12px', padding: '15px', background: colors.surfaceSoft },
  partInfo: { marginBottom: '10px' },
  partActions: { display: 'flex', gap: '8px', justifyContent: 'flex-end' },
  cardActions: { display: 'flex', gap: '10px' },
  btn: { padding: '10px 20px', border: 'none', borderRadius: '999px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  approveBtn: { background: colors.parGreen, color: 'white', border: 'none', borderRadius: '999px', cursor: 'pointer', padding: '8px 16px', fontSize: '14px', fontWeight: '600' },
  rejectBtn: { background: colors.red, color: 'white', border: 'none', borderRadius: '999px', cursor: 'pointer', padding: '8px 16px', fontSize: '14px', fontWeight: '600' },
  tableContainer: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', color: colors.text },
  th: { textAlign: 'left', padding: '12px', borderBottom: `2px solid ${colors.glassBorderStrong}`, fontWeight: '600', color: colors.textMuted },
  tr: { borderBottom: `1px solid ${colors.glassBorder}` },
  td: { padding: '12px' },
  userCell: { display: 'flex', alignItems: 'center', gap: '10px' },
  userAvatar: { width: '30px', height: '30px', borderRadius: '50%' },
  select: { padding: '6px 10px', borderRadius: '8px', border: `1px solid ${colors.glassBorder}`, background: colors.glass, color: colors.text },
  statBadge: { background: 'rgba(47,182,92,0.16)', color: colors.parGreenBright, padding: '4px 12px', borderRadius: '12px', fontSize: '13px', fontWeight: '600' },
  tokenBtn: { background: colors.parLime, color: '#1a1a1a', border: 'none', padding: '6px 12px', borderRadius: '999px', cursor: 'pointer', fontSize: '12px', fontWeight: 700 },
  inlineSpinner: { marginLeft: '6px', fontSize: '12px' },
  partCodeLink: {
    color: colors.parGreenBright,
    fontFamily: 'monospace',
    cursor: 'pointer',
    textDecoration: 'underline',
    fontWeight: '600',
    transition: 'color 0.2s'
  },
  requestsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '20px',
    marginTop: '20px'
  },
  requestCard: {
    background: colors.surfaceSoft,
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
    border: `1px solid rgba(47,182,92,0.45)`
  },
  requestHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
    paddingBottom: '10px',
    borderBottom: `1px solid ${colors.glassBorder}`
  },
  requestType: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: colors.parGreenBright
  },
  requestDate: {
    fontSize: '12px',
    color: colors.textFaint
  },
  requestInfo: {
    marginBottom: '15px'
  },
  requestMake: {
    marginBottom: '8px',
    fontSize: '14px',
    color: colors.text
  },
  requestModel: {
    fontSize: '14px',
    color: colors.text
  },
  requestUser: {
    marginBottom: '15px',
    fontSize: '13px',
    color: colors.textMuted
  },
  requestUserEmail: {
    fontSize: '12px',
    color: colors.textFaint,
    marginLeft: '5px'
  },
  requestActions: {
    display: 'flex',
    gap: '10px'
  },
  customBadge: {
    display: 'inline-block',
    background: colors.parLime,
    color: '#1a1a1a',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 'bold',
    marginLeft: '8px',
    marginRight: '4px'
  },
  customNote: {
    fontSize: '13px',
    color: colors.parGreenBright,
    marginTop: '8px',
    padding: '8px',
    background: 'rgba(47,182,92,0.12)',
    borderRadius: '8px',
    fontWeight: '500'
  },
  readOnlyTier: {
    padding: '5px 10px',
    background: colors.glass,
    borderRadius: '8px',
    fontSize: '14px',
    color: colors.textMuted,
    fontWeight: '600'
  },
  readOnlySalesRole: {
    padding: '5px 10px',
    background: colors.glass,
    borderRadius: '8px',
    fontSize: '14px',
    color: colors.textMuted
  },
  inboxToggleBtn: {
    border: 'none',
    borderRadius: '12px',
    padding: '4px 12px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '700',
    transition: 'all 0.2s'
  }
};

export default Admin;
